import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getBreakpointUp,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { Fragment, useState } from 'react'
import Collapsible from 'src/components/Collapsible/Collapsible'
import CopyTextLink from 'src/components/CopyTextLink/CopyTextLink'
import ExpiresTooltip from 'src/components/ExpiresTooltip/ExpiresTooltip'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import SvgCopy from 'src/components/Svgs/SvgCopy'
import { useTranslate } from 'src/localization/Trns'
import {
  ScreeningStatus,
  VirtualCardSecureModel,
} from 'src/models/virtualCard.models'
import { VirtualCardPermissionsModel } from 'src/models/virtualCardPermissions.models'
import styled, { css, CSSProp } from 'styled-components'

interface VirtualCardPaymentInfoProps {
  virtualCard: VirtualCardSecureModel
  virtualCardPermissions?: VirtualCardPermissionsModel
  recipientTruncatedFullNameOrEmail: string | undefined
  hideCardDetails?: boolean
  pendingFinalApproval?: boolean
  showPushToWallet?: boolean
}

const InfoDiv = styled.div<{
  cardNum?: boolean
  cvc?: boolean
  name?: boolean
  billingAddress?: boolean
}>`
  display: flex;
  ${getBreakpointUp(breakpointSizes.XSM)(css`
    max-width: 180px;
    flex-direction: column;
    &:nth-child(n + 2) {
      margin-left: 36px;
    }
  `)}
  ${getBreakpointOnly(breakpointSizes.XXSM)(css`
    max-width: none;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;

    &:nth-child(n + 2) {
      margin-left: 0px;
      margin-top: 8px;
    }
  `)}
  ${({ cardNum }): CSSProp => {
    return cardNum
      ? css`
          min-width: 180px;
        `
      : css``
  }}
  ${({ cvc }): CSSProp => {
    return cvc
      ? css`
          ${getBreakpointOnly(breakpointSizes.SM)(css`
            min-width: 180px;
          `)}
        `
      : css``
  }}
  ${({ name }): CSSProp => {
    return name
      ? css`
          min-width: 180px;
          margin-top: 36px;
          ${getBreakpointOnly(breakpointSizes.SM)(css`
            margin-top: 0px;
            margin-bottom: 36px;
          `)}
          ${getBreakpointDown(breakpointSizes.SM)(css`
            margin-top: 0px;
          `)}
        `
      : css``
  }}
  ${({ billingAddress }): CSSProp => {
    return billingAddress
      ? css`
          ${getBreakpointUp(breakpointSizes.SM)(css`
            margin-top: 36px;
          `)}
          ${getBreakpointOnly(breakpointSizes.SM)(css`
            margin-top: 0px;
            margin-bottom: 36px;
          `)}
        ${getBreakpointUp(breakpointSizes.XSM)(css`
            max-width: 360px;
          `)}
        ${getBreakpointOnly(breakpointSizes.XXSM)(css`
            margin-top: 0px;
          `)}
        `
      : css``
  }}
`

const InfoHeaderSpan = styled.span`
  ${getFontCss(
    14,
    styleConstants.colors.gray_darkest,
    styleConstants.fontFamilies.regular,
  )};
  display: flex;
  white-space: nowrap;
`

const InfoBodySpan = styled.span`
  margin-top: 4px;
  ${getFontCss(
    14,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )};
  overflow: hidden;
  white-space: normal;
  text-overflow: ellipsis;
  ${getBreakpointOnly(breakpointSizes.XXSM)(css`
    text-align: right;
    margin-top: 0px;
    margin-left: 12px;
  `)}
`

const AddressLabelSpan = styled.span`
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledChevronUp = styled(SvgChevronUp)`
  transform: scale(0.7);
`

const StyledChevronDown = styled(SvgChevronDown)`
  transform: scale(0.7);
`

const VirtualCardPaymentInfo: React.FC<VirtualCardPaymentInfoProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const [isFullInfoOpen, setIsFullInfoOpen] = useState(false)
  const onOpenFullPaymentInfoClick = (): void => {
    setIsFullInfoOpen(!isFullInfoOpen)
  }

  return (
    <div
      css={css`
        background-color: ${styleConstants.colors.gray_lightest};
        display: flex;
        flex-direction: column;
        ${getBreakpointUp(breakpointSizes.XLG)(css`
          padding: ${props.showPushToWallet
              ? styleConstants.heights.cspTopbar
              : styleConstants.heights.headerPaddingLGUp}
            ${styleConstants.widths.headerPaddingXLGUp};
        `)}
        ${getBreakpointOnly(breakpointSizes.LG)(css`
          padding: ${props.showPushToWallet
              ? styleConstants.heights.cspTopbar
              : styleConstants.heights.headerPaddingLGUp}
            ${styleConstants.widths.headerPaddingLGOnly};
        `)}
      ${getBreakpointOnly(breakpointSizes.MD)(css`
          padding: ${styleConstants.heights.headerPaddingMDDown}
            ${styleConstants.widths.headerPaddingMDOnly};
        `)}
      ${getBreakpointDown(breakpointSizes.SM)(css`
          padding: 0px;
        `)}
      `}
    >
      {props.virtualCard.isPending && (
        <span
          css={css`
            ${getFontCss(
              40,
              styleConstants.colors.font_gray_light,
              styleConstants.fontFamilies.regular,
            )};
            ${getBreakpointOnly(breakpointSizes.SM)(css`
              padding: ${styleConstants.heights.headerPaddingMDDown}
                ${styleConstants.widths.headerPaddingSMOnly};
            `)}
            ${getBreakpointOnly(breakpointSizes.XSM)(css`
              padding: ${styleConstants.heights.headerPaddingMDDown}
                ${styleConstants.widths.headerPaddingXSMOnly};
            `)}
          ${getBreakpointOnly(breakpointSizes.XXSM)(css`
              padding: ${styleConstants.heights.headerPaddingMDDown}
                ${styleConstants.widths.headerPaddingXXSMOnly};
            `)}
          `}
        >
          {translate(
            props.pendingFinalApproval
              ? 'Pending Final Approval'
              : 'Pending Approval',
          )}
        </span>
      )}
      {!props.virtualCard.isPending && (
        <Fragment>
          <div
            css={css`
              display: flex;
              ${getBreakpointUp(breakpointSizes.MD)(css`
                align-items: flex-start;
              `)}
              ${getBreakpointOnly(breakpointSizes.SM)(css`
                flex-direction: column;
                align-items: stretch;
                padding: ${styleConstants.heights.headerPaddingMDDown}
                  ${styleConstants.widths.headerPaddingSMOnly};
              `)}
            ${getBreakpointOnly(breakpointSizes.XSM)(css`
                align-items: flex-end;
                padding: ${styleConstants.heights.headerPaddingMDDown}
                  ${styleConstants.widths.headerPaddingXSMOnly};
              `)}
            ${getBreakpointOnly(breakpointSizes.XXSM)(css`
                flex-direction: column;
                align-items: stretch;
                padding: ${styleConstants.heights.headerPaddingMDDown}
                  ${styleConstants.widths.headerPaddingXXSMOnly};
              `)}
            `}
          >
            <div
              css={css`
                display: flex;
                z-index: 1;
                ${getBreakpointOnly(breakpointSizes.XXSM)(css`
                  flex-direction: column;
                  width: 100%;
                `)}
              `}
            >
              <InfoDiv cardNum>
                <InfoHeaderSpan id="cardNumberHeader">
                  {translate('Card Number')}
                  {props.virtualCard.isActive &&
                    props.virtualCardPermissions?.canSpend &&
                    (props.virtualCard.screening?.status === undefined ||
                      props.virtualCard.screening.status ===
                        ScreeningStatus.Pass ||
                      props.virtualCard.screening.status ===
                        ScreeningStatus.NotApplicable) && (
                      <CopyTextLink
                        css={css`
                          margin-top: -6px;
                          margin-left: 4px;
                          ${getBreakpointDown(breakpointSizes.XSM)(css`
                            display: none;
                          `)}
                        `}
                        copyValue={props.virtualCard.activeOrRedactedVcn}
                        copyText={
                          <>
                            <SvgCopy
                              css={css`
                                transform: scale(0.6);
                              `}
                            />
                            {translate('Copy')}
                          </>
                        }
                      />
                    )}
                </InfoHeaderSpan>
                <InfoBodySpan id="cardNumberValue">
                  {props.hideCardDetails
                    ? props.virtualCard.redactedVcn
                    : props.virtualCard.formattedCardNumber}
                </InfoBodySpan>
              </InfoDiv>
              <InfoDiv>
                <InfoHeaderSpan id="expiresHeader">
                  {translate('Expires')}
                  <ExpiresTooltip
                    css={css`
                      height: 16px;
                    `}
                    openLeft={false}
                  />
                </InfoHeaderSpan>
                <InfoBodySpan id="expiresValue">
                  {props.hideCardDetails
                    ? 'XX/XX'
                    : props.virtualCard.expirationString}
                </InfoBodySpan>
              </InfoDiv>
            </div>
            <div
              css={css`
                display: flex;
                ${getBreakpointUp(breakpointSizes.MD)(css`
                  margin-left: 36px;
                `)}
                ${getBreakpointUp(breakpointSizes.SM)(css`
                  align-items: flex-end;
                `)}
              ${getBreakpointOnly(breakpointSizes.SM)(css`
                  margin-top: 36px;
                `)}
              ${getBreakpointOnly(breakpointSizes.XSM)(css`
                  margin-left: 36px;
                `)}
              ${getBreakpointOnly(breakpointSizes.XXSM)(css`
                  margin-top: 8px;
                  align-items: flex-start;
                  flex-direction: column;
                  width: 100%;
                `)}
              `}
            >
              {!props.virtualCard.isInactive &&
                props.virtualCardPermissions?.canSpend &&
                props.virtualCard.securityCode !== undefined && (
                  <InfoDiv cvc>
                    <InfoHeaderSpan id="cvcHeader">
                      {translate('CVC')}
                    </InfoHeaderSpan>
                    <InfoBodySpan id="cvcValue">
                      {props.hideCardDetails
                        ? 'XXX'
                        : props.virtualCard.securityCode}
                    </InfoBodySpan>
                  </InfoDiv>
                )}
              <InfoDiv>
                <InfoHeaderSpan id="zipcodeHeader">
                  {translate('Zipcode')}
                </InfoHeaderSpan>
                <InfoBodySpan id="zipcodeValue">
                  {props.hideCardDetails
                    ? 'XXXXX'
                    : props.virtualCard.address?.postal}
                </InfoBodySpan>
              </InfoDiv>
              <span
                css={css`
                  ${getFontCss(
                    12,
                    themeConstants.colors.cta,
                    styleConstants.fontFamilies.regular,
                  )};
                  height: 18px;
                  display: flex;
                  align-items: center;
                  cursor: pointer;
                  ${getBreakpointUp(breakpointSizes.LG)(css`
                    margin-left: 36px;
                  `)}
                  ${getBreakpointOnly(breakpointSizes.MD)(css`
                    margin-left: 24px;
                  `)}
                  ${getBreakpointDown(breakpointSizes.SM)(css`
                    display: none;
                  `)}
                `}
                onClick={onOpenFullPaymentInfoClick}
                onKeyPress={onOpenFullPaymentInfoClick}
                role="button"
                tabIndex={0}
              >
                {!isFullInfoOpen && <StyledChevronDown />}
                {isFullInfoOpen && <StyledChevronUp />}
                {!isFullInfoOpen && translate('Name and Address')}
                {isFullInfoOpen && translate('Hide Name and Address')}
              </span>
            </div>
          </div>
          <Collapsible isOpen={isFullInfoOpen}>
            <div
              css={css`
                display: flex;
                ${getBreakpointOnly(breakpointSizes.SM)(css`
                  padding: 0px ${styleConstants.widths.headerPaddingSMOnly};
                  overflow: hidden;
                `)}
                ${getBreakpointOnly(breakpointSizes.XSM)(css`
                  padding: 0px ${styleConstants.widths.headerPaddingXSMOnly}
                    ${styleConstants.heights.headerPaddingMDDown};
                  overflow: hidden;
                `)}
              ${getBreakpointOnly(breakpointSizes.XXSM)(css`
                  padding: 0px ${styleConstants.widths.headerPaddingXXSMOnly};
                  overflow: hidden;
                  flex-direction: column;
                `)}
              `}
            >
              <InfoDiv name>
                <InfoHeaderSpan id="nameOnCardHeader">
                  {translate('Name on Card')}
                </InfoHeaderSpan>
                <InfoBodySpan id="nameOnCardValue">
                  {props.recipientTruncatedFullNameOrEmail ?? '--'}
                </InfoBodySpan>
              </InfoDiv>
              <InfoDiv billingAddress>
                <InfoHeaderSpan id="billingAddressHeader">
                  {translate('Billing Address')}
                </InfoHeaderSpan>
                <InfoBodySpan id="billingAddressValue">
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                    `}
                  >
                    <AddressLabelSpan>
                      {props.virtualCard.address?.address1}
                    </AddressLabelSpan>
                    {props.virtualCard.address?.address2 !== undefined && (
                      <AddressLabelSpan>
                        {props.virtualCard.address.address2}
                      </AddressLabelSpan>
                    )}
                    <AddressLabelSpan>
                      {`${props.virtualCard.address?.city}, ${props.virtualCard.address?.province}, ${props.virtualCard.address?.postal}`}
                    </AddressLabelSpan>
                    <AddressLabelSpan>
                      {props.virtualCard.address?.country}
                    </AddressLabelSpan>
                  </div>
                </InfoBodySpan>
              </InfoDiv>
            </div>
          </Collapsible>
          <div
            css={css`
              cursor: pointer;
              border-top: 1px solid ${styleConstants.colors.gray_light};
              ${getBreakpointUp(breakpointSizes.MD)(css`
                display: none;
              `)}
              ${getBreakpointOnly(breakpointSizes.SM)(css`
                padding: 20px ${styleConstants.widths.headerPaddingSMOnly};
              `)}
              ${getBreakpointOnly(breakpointSizes.XSM)(css`
                padding: 20px ${styleConstants.widths.headerPaddingXSMOnly};
              `)}
              ${getBreakpointOnly(breakpointSizes.XXSM)(css`
                padding: 20px ${styleConstants.widths.headerPaddingXXSMOnly};
              `)}
              transition: background-color ${styleConstants.transitionSpeed
                .fast} ease-out;
              span {
                transition: color ${styleConstants.transitionSpeed.fast}
                  ease-out;
              }
              svg path {
                transition: fill ${styleConstants.transitionSpeed.fast} ease-out;
              }
              &:hover {
                background-color: ${themeConstants.colors.cta};
                span {
                  color: ${styleConstants.colors.white};
                }
                svg path {
                  fill: ${styleConstants.colors.white};
                }
              }
            `}
            onClick={onOpenFullPaymentInfoClick}
            onKeyPress={onOpenFullPaymentInfoClick}
            role="button"
            tabIndex={0}
          >
            <span
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.cta,
                  styleConstants.fontFamilies.medium,
                )};
                height: 18px;
                display: flex;
                align-items: center;
              `}
            >
              {!isFullInfoOpen && <StyledChevronDown />}
              {isFullInfoOpen && <StyledChevronUp />}
              {!isFullInfoOpen && translate('Name and Address')}
              {isFullInfoOpen && translate('Hide Name and Address')}
            </span>
          </div>
        </Fragment>
      )}
    </div>
  )
}

export default VirtualCardPaymentInfo
